import React, { useState, useEffect, useRef } from "react";
import { getLocationsByPostcode, getLocationById } from "@/app/actions";
import { RecordModel } from 'pocketbase';

interface LocationInputProps {
  label?: string;
  initialLocationId?: string;
  placeholder?: string;
  onChange?: (location: RecordModel) => void;
}

const LocationInput: React.FC<LocationInputProps> = ({
  initialLocationId,
  label,
  placeholder = "Postcode or Suburb",
  onChange,
}) => {
  const [selectedLocation, setSelectedLocation] = useState<RecordModel | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [locationSuggestions, setLocationSuggestions] = useState<RecordModel[]>([]);
  const [isSuggestionsVisible, setIsSuggestionsVisible] = useState(false);
  const [defaultLocationName, setDefaultLocationName] = useState("");
  const [cache, setCache] = useState<{ [key: string]: RecordModel[] }>({});
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchInitialLocation = async () => {
      if (initialLocationId) {
        const location = await getLocationById(initialLocationId);
        if (location) {
          setSelectedLocation(location);
          setSearchTerm(location.locationName);
          setDefaultLocationName(location.locationName);
        }
      }
    };
    fetchInitialLocation();
  }, [initialLocationId]);

  const fetchLocations = async (term: string) => {
    if (cache[term]) {
      setLocationSuggestions(cache[term]);
    } else {
      const locations = await getLocationsByPostcode(term);
      setCache((prevCache) => ({ ...prevCache, [term]: locations }));
      setLocationSuggestions(locations);
    }
  };

  const handleInputChange = (value: string) => {
    setSearchTerm(value);
    setIsSuggestionsVisible(true);

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (value.length >= 3) {
      debounceTimeoutRef.current = setTimeout(() => {
        fetchLocations(value);
      }, 300);
    } else {
        setLocationSuggestions([]);
    }
  };

  const handleLocationSelect = (location: RecordModel) => {
    setSelectedLocation(location);
    setSearchTerm(location.locationName);
    setDefaultLocationName(location.locationName);
    setLocationSuggestions([]);
    setIsSuggestionsVisible(false);
    onChange && onChange(location);
  };

  const handleInputClick = () => {
    // setSearchTerm(""); // Don't clear searchTerm on click
    if (!selectedLocation) {
      setIsSuggestionsVisible(true); // Show suggestions if no location is selected
    }
  };

    const handleBlur = () => {
    setTimeout(() => {
      setIsSuggestionsVisible(false);
      if (!selectedLocation) {
        setSearchTerm(defaultLocationName);
          setLocationSuggestions([]); // Clear suggestions on blur if no location selected
      }
    }, 300);
  };

  return (
    <div className="flex flex-col relative">
      {label && <label className="text-md font-bold mb-2">{label}</label>}
      <input
        type="text"
        placeholder={placeholder}
        value={searchTerm}
        ref={inputRef}
        onClick={handleInputClick}
        onBlur={handleBlur}
        onChange={(e) => handleInputChange(e.target.value)}
        className="input input-bordered w-full mb-4"
      />
      {isSuggestionsVisible && locationSuggestions.length > 0 && (
        <ul className="absolute bg-white border border-gray-300 w-full max-h-40 overflow-y-auto z-10 translate-y-10">
          {locationSuggestions.map((suggestion) => (
            <li
              key={suggestion.id}
              className="p-2 cursor-pointer hover:bg-gray-200"
              onClick={() => handleLocationSelect(suggestion)}
            >
              {suggestion.locationName +
                `${locationSuggestions.filter((x) => x.locationName === suggestion.locationName).length > 1
                  ? ` (${suggestion.postcode})`
                  : ``
                }`}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LocationInput;